/* eslint-disable react/prop-types */
import React, { useState } from 'react';
import { Form, Input, Button, DatePicker, Select, InputNumber, Radio, Checkbox } from 'antd';
import styled from 'styled-components';
import moment from 'moment';
import CampaignLabelSelect from '../CampaignLabelSelect';
import CampaignSegmentSelect from '../CampaignSegmentSelect';
import SitewidePromoFields, { isFixterDeployment } from '../SitewidePromoFields';

const { Option } = Select;
const { Item } = Form;

const dateTimeFormat = 'YYYY-MM-DD HH:mm';

const Styled = styled(Form)`
  margin-top: 50px;
  .ant-form-item.-couponOrDiscount .ant-form-item-children-icon {
    display: none;
  }

  /* Long labels stay inside the label column instead of clipping into the sidebar. */
  .ant-form-item-label {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    float: left;
    box-sizing: border-box;
    white-space: normal;
    overflow: hidden;
    height: auto;
    min-height: 40px;
    min-width: 0;
    line-height: 1.4;
    padding-left: 24px;
    padding-right: 16px;
  }

  .ant-form-item-label > label {
    white-space: normal;
    height: auto;
    max-width: 100%;
    min-width: 0;
    line-height: 1.4;
  }
`;

const isHtml = (input) => /<(“[^”]*”|'[^’]*’|[^'”>])*>/i.test(input);

const CampaignCreateForm = ({ form, onSubmit, onSitewideState }) => {
  const [market, setMarket] = useState();
  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(e, form);
  };

  const { getFieldDecorator, getFieldValue, setFieldsValue } = form;

  const formItemLayout = {
    labelCol: {
      xs: { span: 24 },
      sm: { span: 5 },
    },
    wrapperCol: {
      xs: { span: 24 },
      sm: { span: 12 },
    },
  };

  const tailFormItemLayout = {
    wrapperCol: {
      xs: {
        span: 24,
        offset: 0,
      },
      sm: {
        span: 16,
        offset: 4,
      },
    },
  };

  const disabledDate = (current) => current && current < moment().subtract(1, 'days').endOf('day');
  const onRadioChange = () => {
    if (getFieldValue('couponOrDiscount') === 'coupon') {
      return setFieldsValue({
        discountAmountPercentage: null,
      });
    }
    return setFieldsValue({
      discountAmountPence: null,
    });
  };

  return (
    <Styled {...formItemLayout} onSubmit={handleSubmit}>
      <Item label="Banner" hasFeedback>
        {getFieldDecorator('name', {
          rules: [
            {
              required: true,
            },
          ],
        })(<Input placeholder="e.g 20% off your next booking - thank you" />)}
      </Item>
      <Item label="Market" hasFeedback>
        {getFieldDecorator('segment', {
          rules: [
            {
              required: true,
            },
          ],
          getValueFromEvent: (value) => {
            setMarket(value);
            return value;
          },
        })(<CampaignSegmentSelect placeholder="Select a market" />)}
      </Item>
      <Item label="Code" hasFeedback>
        {getFieldDecorator('code', {
          rules: [
            {
              required: true,
            },
            {
              validator: async (_, value) => {
                if (isHtml(value)) {
                  throw new Error('HTML is not allowed in this field!');
                }
              },
            },
          ],
        })(<Input placeholder="e.g THANKYOU" />)}
      </Item>
      <Item label="Start" hasFeedback>
        {getFieldDecorator('validFromDate', {
          rules: [
            {
              required: true,
            },
          ],
        })(
          <DatePicker
            size="small"
            showTime={{ defaultValue: moment('00:00', 'HH:mm') }}
            disabledDate={disabledDate}
            format={dateTimeFormat}
            placeholder="Valid from date"
          />
        )}
      </Item>
      <Item label="End" hasFeedback>
        {getFieldDecorator('validUntilDate', {
          rules: [
            {
              required: true,
            },
          ],
        })(
          <DatePicker
            size="small"
            showTime={{ defaultValue: moment('00:00', 'HH:mm') }}
            disabledDate={disabledDate}
            format={dateTimeFormat}
            placeholder="Valid until date"
          />
        )}
      </Item>
      <Item label="Coupon £ OR Discount %" hasFeedback className="-couponOrDiscount" onChange={onRadioChange}>
        {getFieldDecorator('couponOrDiscount', {
          rules: [
            {
              required: false,
            },
          ],
          initialValue: 'coupon',
        })(
          <Radio.Group>
            <Radio value="coupon">Coupon £</Radio>
            <Radio value="discount">Discount %</Radio>
          </Radio.Group>
        )}
      </Item>
      <Item label="Coupon £" hasFeedback>
        {getFieldDecorator('discountAmountPence', {
          rules: [
            {
              required: false,
            },
          ],
        })(<InputNumber min={0} max={100} disabled={getFieldValue('couponOrDiscount') === 'discount'} />)}
      </Item>
      <Item label="Discount %" hasFeedback>
        {getFieldDecorator('discountAmountPercentage', {
          rules: [
            {
              required: false,
            },
          ],
        })(<InputNumber min={0} max={100} disabled={getFieldValue('couponOrDiscount') === 'coupon'} />)}
      </Item>
      <Item label="Apply discount from checkout">
        {getFieldDecorator('appliedToCheckout', {
          rules: [
            {
              required: false,
            },
          ],
        })(<Checkbox defaultChecked />)}
      </Item>
      <Item label="Apply discount from Maia & EWO">
        {getFieldDecorator('appliedToEWO', {
          rules: [
            {
              required: false,
            },
          ],
        })(<Checkbox defaultChecked />)}
      </Item>
      {isFixterDeployment() && (
        <SitewidePromoFields
          mode="create"
          segment={market || getFieldValue('segment')}
          onState={onSitewideState}
        />
      )}
      <Item label="Single Use" hasFeedback>
        {getFieldDecorator('singleUse', {
          rules: [
            {
              required: false,
            },
          ],
        })(
          <Select placeholder="Single use - boolean">
            <Option value>True</Option>
            <Option value={false}>False</Option>
          </Select>
        )}
      </Item>
      <Item label="Label" hasFeedback>
        {getFieldDecorator('label', {
          rules: [
            {
              required: true,
            },
          ],
        })(<CampaignLabelSelect placeholder="Select an option for label" />)}
      </Item>

      <Item {...tailFormItemLayout}>
        <Button type="primary" htmlType="submit">
          Save
        </Button>
      </Item>
    </Styled>
  );
};

export default (mapPropsToFields) =>
  Form.create({ name: 'campaign-create', mapPropsToFields })(CampaignCreateForm);
